import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2, XCircle } from 'lucide-react'
import {
  CalendarApiError,
  type CalendarActivity,
  type MoveCheck,
} from '@/api/resourceCalendarApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCalendarMutations } from '@/hooks/useResourceCalendar'
import { UNASSIGNED, dayHeader, formatHours } from '@/lib/myTasks'
import { TypeBadge } from './ActivityCard'

export interface MoveTarget {
  date: string
  ownerId: string | null
}

/**
 * Confirm a move, assignment or reassignment. Nothing changes until Confirm is
 * pressed (a reason is optional): the proposal is checked with the server
 * first, refusals (unqualified, unavailable, changed since read) block it and
 * warnings (past due, over capacity) are shown and may be accepted.
 *
 * `editable` adds day and owner fields — the keyboard and touch way to move a
 * card, and the Assign / Reassign action of the equipment queue.
 */
export function MoveDialog({
  activity,
  initial,
  editable,
  lockDate,
  title,
  people,
  ownerName,
  onClose,
}: {
  activity: CalendarActivity
  initial: MoveTarget
  editable: boolean
  /** Assigning keeps the day: only the owner may change. */
  lockDate?: boolean
  title: string
  people: { userId: string; name: string }[]
  ownerName: (id: string | null) => string
  onClose: () => void
}) {
  const { move, preview } = useCalendarMutations()
  const [target, setTarget] = useState<MoveTarget>(initial)
  const [reason, setReason] = useState('')
  const [check, setCheck] = useState<MoveCheck | null>(null)
  const [checking, setChecking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const unchanged = target.date === activity.date && target.ownerId === activity.ownerId

  // Ask the server what this move would hit, every time the proposal changes.
  useEffect(() => {
    if (unchanged || !target.date) {
      setCheck(null)
      return
    }
    let live = true
    setChecking(true)
    preview(activity.key, { ...target, version: activity.version })
      .then((c) => live && setCheck(c))
      .catch((e: unknown) =>
        live && setCheck({ errors: [e instanceof Error ? e.message : 'Could not check this move.'], warnings: [] }),
      )
      .finally(() => live && setChecking(false))
    return () => {
      live = false
    }
    // `preview` is a plain API call; re-run only when the proposal changes.
  }, [activity.key, activity.version, target.date, target.ownerId, unchanged])

  const blocked = unchanged || checking || !check || check.errors.length > 0

  const confirm = () => {
    setFailure(null)
    move.mutate(
      { key: activity.key, body: { ...target, version: activity.version, reason: reason.trim() || undefined } },
      {
        onSuccess: onClose,
        onError: (e) =>
          setFailure(
            e instanceof CalendarApiError && e.status === 409
              ? 'Someone changed this activity while you were looking at it. The calendar has been refreshed; open it again to try once more.'
              : e.message,
          ),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className='flex items-center gap-1.5'>
            <TypeBadge code={activity.type} />
            <span className='truncate'>{activity.description}</span>
            <span className='shrink-0 tabular-nums'>· {formatHours(activity.hours)} h</span>
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3 text-sm'>
          <div className='grid grid-cols-[5.5rem_1fr] items-center gap-x-3 gap-y-1 text-muted-foreground'>
            <span>From</span>
            <span className='text-foreground'>
              {dayHeader(activity.date)} · {ownerName(activity.ownerId)}
            </span>
            {!editable && (
              <>
                <span>To</span>
                <span className='font-medium text-foreground'>
                  {dayHeader(target.date)} · {ownerName(target.ownerId)}
                </span>
              </>
            )}
          </div>

          {editable && (
            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1'>
                <Label htmlFor='move-date'>Day</Label>
                <Input
                  id='move-date'
                  type='date'
                  value={target.date}
                  disabled={lockDate}
                  onChange={(e) => setTarget((t) => ({ ...t, date: e.target.value }))}
                />
              </div>
              <div className='space-y-1'>
                <Label>Owner</Label>
                <Select
                  value={target.ownerId ?? UNASSIGNED}
                  onValueChange={(v) => setTarget((t) => ({ ...t, ownerId: v === UNASSIGNED ? null : v }))}
                >
                  <SelectTrigger aria-label='Owner'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {people.map((p) => (
                      <SelectItem key={p.userId} value={p.userId}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {checking && (
            <p className='flex items-center gap-1.5 text-muted-foreground'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' /> Checking…
            </p>
          )}
          {check?.errors.map((e) => (
            <p key={e} className='flex items-start gap-1.5 text-destructive'>
              <XCircle className='mt-0.5 h-3.5 w-3.5 shrink-0' /> {e}
            </p>
          ))}
          {check?.warnings.map((w) => (
            <p key={w} className='flex items-start gap-1.5 text-amber-700'>
              <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' /> {w}
            </p>
          ))}

          <div className='space-y-1'>
            <Label htmlFor='move-reason'>Reason (optional)</Label>
            <Textarea
              id='move-reason'
              rows={2}
              maxLength={500}
              value={reason}
              placeholder='Why is this changing? It is kept in the audit trail.'
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {failure && <p className='text-destructive'>{failure}</p>}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={blocked || move.isPending}>
            {move.isPending ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
